/**
 * Admin Lootbox Test Modal - Test all three curtain tiers side by side
 * Visual testing mode only - no database writes
 * Uses the actual CurtainLootbox component to mirror real implementation
 */

import { useState, useCallback } from 'react';
import { X, RefreshCw, TestTube2, Shield } from 'lucide-react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import CurtainLootbox from '../CurtainLootbox';
import { MYSTERY_TIERS } from '../../utils/constants';
import type { DealProduct, MysteryType } from '../../types';

interface Props {
  onClose: () => void;
}

// Mock products for testing - one for each tier with appropriate timestamps
const createMockProducts = (): Record<MysteryType, DealProduct> => {
  const now = Date.now();

  return {
    super: {
      product_code: 'TEST-SUPER-001',
      name: 'Test Super Mystery Product',
      image_url: null,
      price: 199.99,
      original_price: 399.99,
      discount_percent: 50,
      category: 'as_is',
      store_id: 'test-store-001',
      store_name: 'Test IKEA Store',
      store_city: 'Test City',
      country: 'US',
      first_seen: new Date(now - 1 * 60 * 60 * 1000).toISOString(), // 1 hour ago (super tier)
      last_checked: new Date().toISOString(),
      is_mystery: true,
      mystery_type: 'super',
      is_unlocked: false,
    },
    fresh: {
      product_code: 'TEST-FRESH-001',
      name: 'Test Fresh Mystery Product',
      image_url: null,
      price: 149.99,
      original_price: 299.99,
      discount_percent: 50,
      category: 'as_is',
      store_id: 'test-store-001',
      store_name: 'Test IKEA Store',
      store_city: 'Test City',
      country: 'US',
      first_seen: new Date(now - 12 * 60 * 60 * 1000).toISOString(), // 12 hours ago (fresh tier)
      last_checked: new Date().toISOString(),
      is_mystery: true,
      mystery_type: 'fresh',
      is_unlocked: false,
    },
    standard: {
      product_code: 'TEST-STANDARD-001',
      name: 'Test Standard Mystery Product',
      image_url: null,
      price: 99.99,
      original_price: 199.99,
      discount_percent: 50,
      category: 'as_is',
      store_id: 'test-store-001',
      store_name: 'Test IKEA Store',
      store_city: 'Test City',
      country: 'US',
      first_seen: new Date(now - 36 * 60 * 60 * 1000).toISOString(), // 36 hours ago (standard tier)
      last_checked: new Date().toISOString(),
      is_mystery: true,
      mystery_type: 'standard',
      is_unlocked: false,
    },
  };
};

export default function AdminLootboxTestModal({ onClose }: Props) {
  const { t } = useLanguage();

  // Use a key to force remount of CurtainLootbox components on reset
  const [resetKey, setResetKey] = useState(0);

  // Create fresh mock products on each reset
  const mockProducts = createMockProducts();

  const handleReset = useCallback(() => {
    // Incrementing key forces React to remount components, resetting all state
    setResetKey(prev => prev + 1);
  }, []);

  // No-op handlers - visual testing only, no database writes
  const handleUnlock = useCallback((product: DealProduct, type: MysteryType) => {
    console.log('[Admin Test] Unlock animation triggered for tier:', type, 'product:', product.product_code);
  }, []);

  const handlePurchase = useCallback((type: MysteryType) => {
    console.log('[Admin Test] Purchase flow would open for tier:', type);
  }, []);

  const handleLoginRequired = useCallback(() => {
    // No-op: admin is already logged in
    console.log('[Admin Test] Login required (should not happen for admin)');
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="admin-test-modal-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-label={t('modal.close', 'Close modal')}
      />

      {/* Modal Container */}
      <div className="relative bg-gray-900 rounded-2xl shadow-2xl max-w-5xl w-full p-6 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
              <TestTube2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 id="admin-test-modal-title" className="text-xl font-bold text-white">
                {t('admin.lootboxTest.title', 'Lootbox Animation Tester')}
              </h2>
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <Shield className="w-3.5 h-3.5" />
                <span>{t('admin.lootboxTest.subtitle', 'Admin only - Visual testing mode (no database writes)')}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Reset Button */}
            <button
              onClick={handleReset}
              className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
              title={t('admin.lootboxTest.resetAll', 'Reset All')}
            >
              <RefreshCw className="w-4 h-4" />
              {t('admin.lootboxTest.resetAll', 'Reset All')}
            </button>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg"
              aria-label={t('modal.close', 'Close modal')}
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Tier Labels */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          {(['super', 'fresh', 'standard'] as MysteryType[]).map((tier) => (
            <div key={tier} className="text-center">
              <span className={`text-lg font-bold ${MYSTERY_TIERS[tier].color}`}>
                {MYSTERY_TIERS[tier].icon} {MYSTERY_TIERS[tier].name}
              </span>
              <p className="text-gray-500 text-xs">{MYSTERY_TIERS[tier].ageRange}</p>
            </div>
          ))}
        </div>

        {/* Three Curtains Side by Side */}
        <div className="grid grid-cols-3 gap-4" key={resetKey}>
          {(['super', 'fresh', 'standard'] as MysteryType[]).map((tier, index) => (
            <CurtainLootbox
              key={`${tier}-${resetKey}`}
              product={mockProducts[tier]}
              mysteryType={tier}
              onUnlock={handleUnlock}
              onPurchase={handlePurchase}
              onLoginRequired={handleLoginRequired}
              index={index}
            />
          ))}
        </div>

        {/* Instructions */}
        <div className="mt-6 bg-gray-800 rounded-lg p-4">
          <h3 className="text-white font-medium mb-2">
            {t('admin.lootboxTest.instructions', 'Testing Instructions')}
          </h3>
          <ul className="text-gray-400 text-sm space-y-1">
            <li>- {t('admin.lootboxTest.instruction1', 'Click any curtain to trigger the reveal animation')}</li>
            <li>- {t('admin.lootboxTest.instruction2', 'Each tier has its own colored reveal animation')}</li>
            <li>- {t('admin.lootboxTest.instruction3', 'Use "Reset All" to re-lock curtains for repeated testing')}</li>
            <li>- {t('admin.lootboxTest.instruction4', 'No Rumios are spent and no database writes occur')}</li>
          </ul>
        </div>

        {/* Admin Badge */}
        <div className="mt-4 flex items-center justify-center gap-2 text-purple-400 text-xs">
          <Shield className="w-3.5 h-3.5" />
          <span>{t('admin.lootboxTest.adminOnly', 'This modal is only visible to administrators')}</span>
        </div>
      </div>
    </div>
  );
}
